import {
  DatePeriodSelect,
  DatePeriodSelectProps
} from '@unistocks/components/date-period-select/date-period-select';
import { Column, Row, RowProps } from '@unistocks/components/layout/flex';
import { Paragraph } from '@unistocks/components/text/text';
import { formatCurrency, formatDate, formatPercentage } from '@unistocks/localization';
import { useAxisStyles, useTooltipStyles } from '@unistocks/shared/charts/styles';
import { markerBar } from '@unistocks/shared/charts/tooltip';
import { useIsDarkMode } from '@unistocks/shared/hooks/use-active-color-scheme';
import { useEchartsLocale } from '@unistocks/shared/hooks/use-echarts-locale';
import { color } from '@unistocks/shared/utils/color';
import { createDate, titleCase } from '@unistocks/shared/utils/util';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import React, { useMemo, useRef, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { View, useTheme } from 'tamagui';
import { LineChartHeader } from './line-chart-header';
import { LineChartComparison, LineChartProps } from './types';
import { dateButtonsFilter, getReservedTopPx, getTooltipHeightPx } from './utils';

const LineChart: React.FC<LineChartProps> = ({
  primaryId,
  primaryLabel,
  currency,
  chartData,
  groupBy,
  initialValue,
  summary: {
    changePercent: totalChangePercent,
    changeNominal: totalChangeNominal,
    value: totalValue,
    valueLabel
  },
  firstDate,
  lastDate,
  minDateId,
  maxDateId,
  comparison,
  secondaryComparison,
  isLoading,
  onDateRangeChange,
  headerProps,
  chartContainerProps,
  containerProps,
  showChartSettingsMenu = true,
  chartMenu,
  hasShares = true,
  tooltipValueFractionDigits = 0,
  minimizedDatePeriod,
  datePeriodCustomOptions
}) => {
  const isDarkMode = useIsDarkMode();
  const echartsLocale = useEchartsLocale();
  const chartRef = useRef<ReactECharts>(null);
  const theme = useTheme();

  const tooltipStyles = useTooltipStyles();
  const axisStyles = useAxisStyles();

  const { successHex, positiveHex, negativeHex, areaGradient } = useMemo(() => {
    const successHex = theme.success.val;
    const positiveHex = theme.positive.val;
    const negativeHex = theme.negative.val;

    return {
      successHex,
      positiveHex,
      negativeHex,
      areaGradient: [
        color(successHex)
          .lighten(isDarkMode ? -0.4 : 0.6)
          .toCSSHex(),
        successHex
      ]
    };
  }, [isDarkMode, theme.success.val, theme.positive.val, theme.negative.val]);

  const selectionStart = createDate(firstDate).toISODate();
  const selectionEnd = createDate(lastDate).toISODate();

  const seriesData = useMemo(() => {
    return chartData.map(({ timestamp, value, shares }) => [timestamp, value, shares]);
  }, [chartData]);

  // Shares held at the start of the range. The header summary reports the
  // nominal change as (last − first) × initialShares (NAV gain excluding
  // deposits), so the tooltip uses the same baseline to stay consistent.
  const initialShares = useMemo(() => chartData[0]?.shares ?? 0, [chartData]);

  const comparisonSeriesData = useMemo(() => {
    return (comparison?.data ?? []).map(({ timestamp, value }) => [timestamp, value]);
  }, [comparison?.data]);

  const secondaryComparisonSeriesData = useMemo(() => {
    return (secondaryComparison?.data ?? []).map(({ timestamp, value }) => [timestamp, value]);
  }, [secondaryComparison?.data]);

  const dateFormatOptions = useMemo(
    () =>
      groupBy === 'day'
        ? { month: 'short' as const, day: 'numeric' as const }
        : { year: 'numeric' as const, month: 'short' as const },
    [groupBy]
  );

  // Measured heights used to reserve a pixel band above the line area. The
  // header overlays the top of the chart, and the pinned tooltip renders
  // above it in z-order (ECharts tooltips use z-index 9999999, so the
  // low-zIndex header stays beneath them).
  const [headerHeight, setHeaderHeight] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  // Reserve room above the line area for the overlaid header and the pinned
  // tooltip (rows: date, primary, then one per comparison series with data).
  // Both share the same top band since the tooltip renders above the header.
  const tooltipRows =
    2 +
    (comparisonSeriesData.length > 0 ? 1 : 0) +
    (secondaryComparisonSeriesData.length > 0 ? 1 : 0);
  const tooltipHeight = getTooltipHeightPx(
    tooltipRows,
    tooltipStyles.fontSize,
    tooltipStyles.padding
  );
  const reservedTop = getReservedTopPx(headerHeight, tooltipHeight, chartHeight);

  const option = useMemo<EChartsOption>(
    () => ({
      grid: {
        left: 10,
        right: 10,
        // Inset the plot below the header/tooltip band; the tooltip stays
        // pinned to the top of the view, above the header.
        top: reservedTop,
        bottom: 0,
        containLabel: false
      },
      xAxis: {
        type: 'time',
        axisLine: {
          lineStyle: { color: axisStyles.lineColor }
        },
        axisTick: {
          lineStyle: { color: axisStyles.lineColor }
        },
        axisLabel: {
          fontFamily: axisStyles.fontFamily,
          color: axisStyles.color,
          rotate: axisStyles.rotate,
          hideOverlap: true,
          formatter: (value: number) => titleCase(formatDate(value, dateFormatOptions))
        },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        position: 'left',
        axisLine: {
          show: true,
          lineStyle: { color: axisStyles.lineColor }
        },
        axisTick: { show: true },
        axisLabel: {
          color: axisStyles.color,
          formatter: (value: number) =>
            formatPercentage(value / initialValue - 1, { signDisplay: 'exceptZero' })
        },
        splitLine: {
          lineStyle: { color: axisStyles.lineColor, type: 'dashed', opacity: 0.6 }
        },
        min: 'dataMin',
        max: 'dataMax'
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipStyles.background,
        borderColor: tooltipStyles.borderColor,
        textStyle: {
          color: tooltipStyles.color,
          fontSize: tooltipStyles.fontSize
        },
        // Pin the tooltip to the top of the chart; it follows the cursor on x only.
        // confine keeps the content-sized box inside the chart area.
        confine: true,
        position: (point, _params, _dom, _rect, size) => {
          const contentWidth = size?.contentSize?.[0] ?? 0;
          const viewWidth = size?.viewSize?.[0] ?? 0;
          const x = Math.min(
            Math.max(point[0] - contentWidth / 2, 0),
            Math.max(viewWidth - contentWidth, 0)
          );
          return [x, 0];
        },
        axisPointer: {
          type: 'cross',
          snap: true,
          lineStyle: {
            color: axisStyles.headingColor,
            type: 'dashed',
            width: 1.5
          },
          label: {
            show: true,
            backgroundColor: tooltipStyles.background,
            borderColor: tooltipStyles.borderColor,
            color: tooltipStyles.color,
            fontSize: tooltipStyles.fontSize,
            fontFamily: tooltipStyles.fontFamily,
            fontWeight: 'bold',
            formatter: params => {
              if (params.axisDimension === 'x') {
                return titleCase(formatDate(params.value as number, dateFormatOptions));
              }
              return formatPercentage((params.value as number) / initialValue - 1, {
                signDisplay: 'exceptZero'
              });
            }
          }
        },
        formatter: params => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const formatPercent = (change: number, color: string) =>
            `<span style="font-weight: bold; color: ${color};">${formatPercentage(change, {
              signDisplay: 'exceptZero',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}</span>`;
          const percentColorFor = (change: number) =>
            change > 0 ? positiveHex : change < 0 ? negativeHex : tooltipStyles.color;

          // Find main series data
          const primaryParam = params.find(p => p.seriesName === primaryId) ?? params[0];
          const [timestamp, value] = primaryParam.data as [number, number, number];
          // Portfolios (shares) show the nominal change; assets show the price level.
          // Uses the initial share count to match the header summary
          // ((last − first) × initialShares).
          const rowValue = hasShares ? (value - initialValue) * initialShares : value;
          const formattedValue = hasShares
            ? formatCurrency(rowValue, currency, {
                signDisplay: 'exceptZero',
                maximumFractionDigits: tooltipValueFractionDigits
              })
            : formatCurrency(rowValue, currency, {
                minimumFractionDigits: tooltipValueFractionDigits,
                maximumFractionDigits: tooltipValueFractionDigits
              });
          const changePercent = value / initialValue - 1;

          let html = `
              <div style="display: flex; flex-direction: column; gap: 4px; white-space: nowrap;">
                <div style="font-weight: bold; margin-bottom: 2px; text-transform: capitalize;">
                  ${formatDate(timestamp, { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  ${markerBar(successHex)}
                  <span>${primaryLabel}</span>
                  <span style="margin-left: auto; font-weight: bold;">${formattedValue}</span>
                  ${formatPercent(changePercent, percentColorFor(changePercent))}
                </div>
          `;

          html += renderComparisonRow(
            params,
            comparison,
            initialValue,
            positiveHex,
            negativeHex,
            tooltipStyles.color
          );
          html += renderComparisonRow(
            params,
            secondaryComparison,
            initialValue,
            positiveHex,
            negativeHex,
            tooltipStyles.color
          );

          return `${html}</div>`;
        }
      },
      series: [
        {
          type: 'line',
          name: primaryId,
          data: seriesData,
          smooth: 0.3,
          symbol: 'none',
          cursor: 'default',
          itemStyle: { color: successHex },
          lineStyle: {
            color: successHex,
            width: 2
          },
          areaStyle: {
            origin: 'start',
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: areaGradient[0] },
              { offset: 1, color: areaGradient[1] }
            ]),
            opacity: 0.75
          },
          markPoint: {
            name: 'min-max',
            symbol: 'circle',
            symbolSize: 12,
            label: { show: false },
            data: [
              {
                type: 'max',
                name: 'max',
                itemStyle: { color: positiveHex }
              },
              {
                type: 'min',
                name: 'min',
                itemStyle: { color: negativeHex }
              }
            ],
            tooltip: {
              show: true
            }
          }
        },
        ...(comparisonSeriesData.length > 0 && comparison
          ? [
              {
                type: 'line' as const,
                name: comparison.id,
                data: comparisonSeriesData,
                smooth: 0.3,
                symbol: 'none',
                cursor: 'default',
                itemStyle: { color: comparison.color },
                lineStyle: {
                  color: comparison.color,
                  width: 2,
                  type: 'dashed' as const
                }
              }
            ]
          : []),
        ...(secondaryComparisonSeriesData.length > 0 && secondaryComparison
          ? [
              {
                type: 'line' as const,
                name: secondaryComparison.id,
                data: secondaryComparisonSeriesData,
                smooth: 0.3,
                symbol: 'none',
                cursor: 'default',
                itemStyle: { color: secondaryComparison.color },
                lineStyle: {
                  color: secondaryComparison.color,
                  width: 2,
                  type: 'dashed' as const
                }
              }
            ]
          : [])
      ],
      darkMode: isDarkMode
    }),
    [
      axisStyles.lineColor,
      axisStyles.fontFamily,
      axisStyles.color,
      axisStyles.rotate,
      axisStyles.headingColor,
      tooltipStyles.background,
      tooltipStyles.borderColor,
      tooltipStyles.color,
      tooltipStyles.fontSize,
      tooltipStyles.fontFamily,
      primaryId,
      primaryLabel,
      currency,
      seriesData,
      successHex,
      areaGradient,
      positiveHex,
      negativeHex,
      comparisonSeriesData,
      comparison,
      secondaryComparisonSeriesData,
      secondaryComparison,
      hasShares,
      initialShares,
      tooltipValueFractionDigits,
      isDarkMode,
      dateFormatOptions,
      initialValue,
      reservedTop
    ]
  );

  // Defer creating the ECharts instance until the header and container have
  // been measured. `reservedTop` is part of `option`, and echarts-for-react
  // initializes asynchronously; creating the chart first and updating the
  // option after measurement would call setOption with notMerge, rebuilding
  // the chart and replaying the line enter animation on web.
  const isLayoutMeasured = headerHeight > 0 && chartHeight > 0;

  return (
    <Column pos={'relative'} {...containerProps}>
      {/* Chart header overlays the top of the chart, centered. The pinned
          tooltip stays visible above it via the ECharts tooltip z-index.*/}
      <View
        pos={'absolute'}
        t={0}
        l={0}
        r={0}
        ai={'center'}
        pointerEvents={'none'}
        onLayout={(e: LayoutChangeEvent) => setHeaderHeight(e.nativeEvent.layout.height)}
      >
        <LineChartHeader
          w={'100%'}
          firstDate={firstDate}
          lastDate={lastDate}
          totalChangePercent={totalChangePercent}
          totalChangeNominal={totalChangeNominal}
          totalValue={totalValue}
          currency={currency}
          valueLabel={valueLabel}
          {...headerProps}
        />
      </View>

      <View
        w={'100%'}
        onLayout={(e: LayoutChangeEvent) => setChartHeight(e.nativeEvent.layout.height)}
        {...chartContainerProps}
      >
        {isLayoutMeasured && (
          <ReactECharts
            ref={chartRef}
            echarts={echarts}
            option={option}
            notMerge
            lazyUpdate
            style={{ width: '100%', height: '100%' }}
            opts={{ renderer: 'canvas', locale: echartsLocale }}
          />
        )}
      </View>

      {/* Rendered after the canvas so it can receive pointer events on web. */}
      {showChartSettingsMenu ? chartMenu : null}

      {(comparison || secondaryComparison) && (
        <LineLegend
          mt={'$2'}
          items={[
            { label: primaryLabel, color: successHex, dashed: false },
            ...(comparison && comparisonSeriesData.length > 0
              ? [{ label: comparison.label, color: comparison.color, dashed: true }]
              : []),
            ...(secondaryComparison && secondaryComparisonSeriesData.length > 0
              ? [
                  {
                    label: secondaryComparison.label,
                    color: secondaryComparison.color,
                    dashed: true
                  }
                ]
              : [])
          ]}
        />
      )}

      <DatePeriodSelect
        minimized={minimizedDatePeriod}
        datepicker
        minDate={minDateId}
        maxDate={maxDateId}
        selectionStart={selectionStart}
        selectionEnd={selectionEnd}
        filterOptions={dateButtonsFilter}
        customOptions={datePeriodCustomOptions}
        onChange={onDateRangeChange as DatePeriodSelectProps['onChange']}
        disabled={isLoading}
      />
    </Column>
  );
};

type TooltipSeriesPoint = { seriesName?: string; marker?: unknown; data?: unknown };

const renderComparisonRow = (
  params: unknown,
  entry: LineChartComparison | undefined,
  initialValue: number,
  positiveHex: string,
  negativeHex: string,
  fallbackColor: string
): string => {
  if (!entry || !Array.isArray(params)) return '';

  const entryParam = (params as TooltipSeriesPoint[]).find(p => p.seriesName === entry.id);
  if (!entryParam) return '';

  const [, entryValue] = entryParam.data as [number, number];
  const entryChangePercent = entryValue / initialValue - 1;
  const entryPercentColor =
    entryChangePercent > 0 ? positiveHex : entryChangePercent < 0 ? negativeHex : fallbackColor;
  const entryPercentFormatted = formatPercentage(entryChangePercent, {
    signDisplay: 'exceptZero',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return `
    <div style="display: flex; align-items: center; gap: 8px;">
      ${markerBar(entry.color)}
      <span>${entry.tooltipLabel ?? entry.label}</span>
      <span style="margin-left: auto; font-weight: bold; color: ${entryPercentColor};">${entryPercentFormatted}</span>
    </div>
  `;
};

LineChart.displayName = 'LineChart';

export default LineChart;

type LineLegendItem = { label: string; color: string; dashed: boolean };
type LineLegendProps = RowProps & { items: LineLegendItem[] };

export const LineLegend: React.FC<LineLegendProps> = ({ items, mt }) => {
  return (
    <Row gap={'$4'} flexWrap="wrap" mt={mt}>
      {items.map((item, index) => (
        <Row key={index} alignItems="center" gap={'$2'}>
          <View
            w={24}
            h={0}
            borderTopWidth={2}
            borderStyle={item.dashed ? 'dashed' : 'solid'}
            borderColor={item.color as any}
          />
          <Paragraph size={'$2'}>{item.label}</Paragraph>
        </Row>
      ))}
    </Row>
  );
};

LineLegend.displayName = 'LineLegend';
